import { h, clear, toast, formatDate, emptyState } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { AlertTriangle } from 'lucide';
import { NotificationAPI, SessionAPI, EnrollmentAPI, RegistrationRequestAPI, ServiceAPI, UserAPI } from '../services/endpoints.js';
import { navigate } from '../utils/router.js';
import { Avatar, AVATAR_COLORS } from '../components/avatar.js';

function hashCode(s) { let hash = 0; for (let i = 0; i < s.length; i++) { const ch = s.charCodeAt(i); hash = ((hash << 5) - hash) + ch; hash |= 0; } return Math.abs(hash); }

const roleLabel = (r) => r === 'admin' ? 'administrador' : r === 'instructor' ? 'instructor' : 'beneficiario';

const actionBtn = (label, cls, onclick) =>
  h('button', { class: `btn btn-sm ${cls}`, onclick }, label);

const quickAction = (label, cls, onclick) =>
  h('div', { class: 'quick-action' },
    actionBtn(label, cls, onclick),
  );

const eventCard = (item, opts = {}) => {
  const { title, subtitle, badge, actions } = opts;
  return h('div', { class: 'event-card', tabindex: '0', role: 'button' },
    h('div', { class: 'event-card-body' },
      title ? h('strong', {}, title) : null,
      subtitle ? h('div', { class: 'muted text-sm' }, subtitle) : null,
      badge ? h('div', { style: { marginTop: '4px' } }, badge) : null,
    ),
    actions ? h('div', { class: 'event-card-actions' }, ...actions) : null,
  );
};

const sectionCard = (title, linkTo, content) =>
  h('div', { class: 'card dashboard-section' },
    h('div', { class: 'row-between' },
      h('h2', {}, title),
      linkTo ? h('a', { href: 'javascript:void(0)', class: 'text-sm', onclick: (e) => { e.preventDefault(); navigate(linkTo); } }, 'Ver todos →') : null,
    ),
    content,
  );

export const DashboardPage = async (user) => {
  const view = document.getElementById('view');
  clear(view);

  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, `Hola, ${user.full_name}`),
    h('span', { class: 'badge active' }, roleLabel(user.role))
  ));

  const role = user.role;
  const content = h('div', { class: 'dashboard-grid' });
  view.appendChild(content);

  // Show skeletons while loading
  const skeletonCards = ['section', 'section', 'section'].map(() =>
    h('div', { class: 'card dashboard-section skeleton-card' },
      h('div', { class: 'skeleton skeleton-text skeleton-title', style: { marginBottom: '16px' } }),
      ...Array.from({ length: 3 }, () =>
        h('div', { class: 'skeleton skeleton-text skeleton-line', style: { margin: '10px 0' } }),
      ),
    )
  );
  skeletonCards.forEach(c => content.appendChild(c));

  if (role === 'admin') await renderAdmin(content);
  else if (role === 'instructor') await renderInstructor(content, user);
  else await renderBeneficiary(content);
};

// ─── ADMIN ────────────────────────────────────────────────────────────────────

async function renderAdmin(container) {
  const [pendingRegs, sessions, services, users] = await Promise.all([
    safeAsync(() => RegistrationRequestAPI.list({ status: 'pending' }).then(r => r.data || [])),
    safeAsync(() => SessionAPI.list().then(r => r.data || [])),
    safeAsync(() => ServiceAPI.list().then(r => r.data || [])),
    safeAsync(() => UserAPI.list().then(r => r.data || [])),
  ]);
  clear(container);

  // ── Pending registrations ──
  const regContent = h('div', { class: 'event-list' });
  const regs = pendingRegs.slice(0, 5);
  if (!regs.length) {
    regContent.appendChild(emptyState('No hay registros pendientes.'));
  } else {
    for (const r of regs) {
      const avatarColor = AVATAR_COLORS[hashCode(r.full_name) % AVATAR_COLORS.length];
      regContent.appendChild(
        eventCard(r, {
          title: r.full_name,
          subtitle: `${r.email} · ${r.branch_name}`,
          badge: h('span', { class: 'text-xs muted' }, formatDate(r.created_at)),
          actions: [
            actionBtn('Revisar', 'btn', () => navigate('/registrations')),
          ],
        })
      );
    }
  }
  container.appendChild(sectionCard('Solicitudes de registro pendientes', '/registrations', regContent));

  // ── Upcoming sessions ──
  const sesContent = h('div', { class: 'event-list' });
  const upcoming = sessions
    .filter(s => s.status === 'scheduled' && new Date(s.start_datetime) > new Date())
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
    .slice(0, 5);
  if (!upcoming.length) {
    sesContent.appendChild(emptyState('No hay sesiones programadas.'));
  } else {
    for (const s of upcoming) {
      sesContent.appendChild(
        eventCard(s, {
          title: s.service_name || '—',
          subtitle: `${formatDate(s.start_datetime)} · ${s.instructor_name || 'Sin instructor'}`,
          badge: s.max_capacity ? h('span', { class: 'text-xs muted' }, `${s.occupied_slots || 0}/${s.max_capacity} cupos`) : null,
          actions: [
            actionBtn('Ver', 'btn-outline', () => navigate('/sessions')),
          ],
        })
      );
    }
  }
  container.appendChild(sectionCard('Próximas sesiones', '/sessions', sesContent));

  // ── Items requiring attention ──
  const attentionContent = h('div', { class: 'event-list' });
  const attentionItems = [];

  const noInstructorServices = services.filter(s => !s.instructor_ids || !s.instructor_ids.length);
  if (noInstructorServices.length) {
    attentionItems.push({
      icon: AlertTriangle,
      title: `${noInstructorServices.length} servicio(s) sin instructor asignado`,
      subtitle: 'Asigna instructores para que puedan gestionar sesiones.',
      action: { label: 'Asignar', to: '/services' },
    });
  }

  const nearCapacity = upcoming.filter(s => {
    const occ = s.occupied_slots || 0;
    const max = s.max_capacity || 1;
    return occ / max >= 0.8;
  });
  if (nearCapacity.length) {
    attentionItems.push({
      icon: AlertTriangle,
      title: `${nearCapacity.length} sesión(es) cerca del límite de cupo`,
      subtitle: 'Considera abrir nuevas sesiones para estos servicios.',
      action: { label: 'Ver sesiones', to: '/sessions' },
    });
  }

  if (!attentionItems.length) {
    attentionContent.appendChild(emptyState('Todo en orden. No hay nada que requiera atención.'));
  } else {
    for (const item of attentionItems) {
      attentionContent.appendChild(
        h('div', { class: 'attention-row' },
          h('div', { class: 'attention-row-icon' }, h('span', { html: iconSvg(item.icon, 20) })),
          h('div', { class: 'attention-row-body' },
            h('strong', {}, item.title),
            h('div', { class: 'muted text-sm' }, item.subtitle),
          ),
          h('button', { class: 'btn btn-sm btn-outline', onclick: () => navigate(item.action.to) }, item.action.label),
        )
      );
    }
  }
  container.appendChild(sectionCard('Elementos que requieren atención', null, attentionContent));
}

// ─── INSTRUCTOR ───────────────────────────────────────────────────────────────

async function renderInstructor(container, user) {
  const [sessions] = await Promise.all([
    safeAsync(() => SessionAPI.list().then(r => r.data || [])),
  ]);
  clear(container);

  const now = new Date();

  // ── Upcoming classes ──
  const sesContent = h('div', { class: 'event-list' });
  const upcoming = sessions
    .filter(s => s.status === 'scheduled' && new Date(s.start_datetime) > now)
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
    .slice(0, 5);

  if (!upcoming.length) {
    sesContent.appendChild(emptyState('No tienes sesiones próximas.'));
  } else {
    for (const s of upcoming) {
      sesContent.appendChild(
        eventCard(s, {
          title: s.service_name || '—',
          subtitle: `${formatDate(s.start_datetime)} · ${s.branch_name || ''}`,
          badge: s.max_capacity ? h('span', { class: 'text-xs muted' }, `${s.occupied_slots || 0}/${s.max_capacity} cupos`) : null,
          actions: [
            actionBtn('Ver', 'btn-outline', () => navigate('/sessions')),
          ],
        })
      );
    }
  }
  container.appendChild(sectionCard('Próximas clases', '/sessions', sesContent));

  // ── Sessions needing attendance ──
  const attContent = h('div', { class: 'event-list' });
  const needsAttendance = sessions
    .filter(s => {
      if (s.status !== 'completed') return false;
      const end = new Date(s.start_datetime);
      end.setMinutes(end.getMinutes() + (s.duration_minutes || 60));
      return true;
    })
    .sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime))
    .slice(0, 5);

  if (!needsAttendance.length) {
    attContent.appendChild(emptyState('No hay sesiones pendientes de registrar asistencia.'));
  } else {
    for (const s of needsAttendance) {
      attContent.appendChild(
        eventCard(s, {
          title: s.service_name || '—',
          subtitle: `${formatDate(s.start_datetime)} · ${s.branch_name || ''}`,
          actions: [
            actionBtn('Tomar asistencia', 'btn btn-sm btn-success', () => navigate('/sessions')),
            actionBtn('Ver', 'btn-outline', () => navigate('/sessions')),
          ],
        })
      );
    }
  }
  container.appendChild(sectionCard('Sesiones con asistencia pendiente', '/sessions', attContent));
}

// ─── BENEFICIARY ──────────────────────────────────────────────────────────────

async function renderBeneficiary(container) {
  const [enrollments, notifications] = await Promise.all([
    safeAsync(() => EnrollmentAPI.listMine().then(r => r.data || [])),
    safeAsync(() => NotificationAPI.list(true).then(r => r.data || [])),
  ]);
  clear(container);

  const now = new Date();

  // ── Upcoming enrolled sessions ──
  const sesContent = h('div', { class: 'event-list' });
  const upcoming = enrollments
    .filter(e => {
      const ses = e.session || e;
      return ses.status === 'scheduled' && new Date(ses.start_datetime) > now;
    })
    .sort((a, b) => {
      const sa = a.session || a; const sb = b.session || b;
      return new Date(sa.start_datetime) - new Date(sb.start_datetime);
    })
    .slice(0, 5);

  if (!upcoming.length) {
    sesContent.appendChild(emptyState('No tienes sesiones próximas.'));
  } else {
    for (const e of upcoming) {
      const ses = e.session || e;
      sesContent.appendChild(
        eventCard(e, {
          title: ses.service_name || '—',
          subtitle: `${formatDate(ses.start_datetime)} · ${ses.instructor_name || '—'}`,
          actions: [
            actionBtn('Ver', 'btn-outline', () => navigate('/sessions')),
          ],
        })
      );
    }
  }
  container.appendChild(sectionCard('Tus próximas sesiones', '/sessions', sesContent));

  // ── Recent notifications ──
  const notifContent = h('div', { class: 'event-list' });
  const recent = notifications.slice(0, 5);

  if (!recent.length) {
    notifContent.appendChild(emptyState('No tienes notificaciones recientes.'));
  } else {
    for (const n of recent) {
      notifContent.appendChild(
        eventCard(n, {
          title: n.title,
          subtitle: n.message,
          badge: h('span', { class: 'text-xs muted' }, formatDate(n.created_at)),
        })
      );
    }
  }
  container.appendChild(sectionCard('Notificaciones recientes', null, notifContent));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function safeAsync(fn) {
  try { return await fn(); } catch { return []; }
}
